import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '40px 20px',
      backgroundColor: 'var(--white)',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    }}>
      <h1 style={{
        color: 'var(--dark-blue)',
        textAlign: 'center',
        marginBottom: '30px',
        fontSize: '32px'
      }}>
        Privacy Policy
      </h1>

      <div style={{ marginBottom: '20px', textAlign: 'center', color: 'var(--gray-dark)' }}>
        <p><strong>Effective Date:</strong> January 29, 2026</p>
        <p><strong>Last Updated:</strong> January 29, 2026</p>
      </div>

      <div style={{ fontSize: '16px' }}>
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>1. Introduction</h2>
          <p>
            Welcome to <strong>CalorienSweat</strong> ("we," "our," or "us"). CalorienSweat is an India-based
            platform designed to help users manage fitness, nutrition, and wellness programs, and to
            enable communication between users and coaches through approved channels such as WhatsApp.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information
            when you use our platform. By using CalorienSweat, you agree to the collection and use of
            information in accordance with this Privacy Policy.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>2. Information We Collect</h2>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>2.1 Personal Information</h3>
          <p>We may collect the following personal information, as required to provide our services:</p>
          <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
            <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
            <li><strong>Profile Information (optional):</strong> Gender, address, program preferences</li>
            <li><strong>Wellness Information (optional):</strong> Dietary preferences, fitness goals, health metrics you choose to provide</li>
          </ul>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>2.2 Communication Information</h3>
          <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
            <li>Messages exchanged with us via WhatsApp or within the platform</li>
            <li>Feedback, support requests, and responses</li>
          </ul>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>2.3 Automatically Collected Information</h3>
          <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage data (pages visited, time spent, features used)</li>
            <li>Log files for security, troubleshooting, and analytics</li>
            <li>Cookies and similar technologies (see Section 9)</li>
          </ul>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>3. How We Use Your Information</h2>
          <p>We use your information only for legitimate business purposes, including:</p>
          <ul style={{ marginLeft: '20px' }}>
            <li>Provide and maintain the CalorienSweat platform</li>
            <li>Create and manage user accounts</li>
            <li>Facilitate coach-client communication</li>
            <li>Deliver wellness program features (fitness, nutrition, and progress tracking)</li>
            <li>Send service-related notifications and updates</li>
            <li>Improve platform performance and user experience</li>
            <li>Ensure security and prevent fraud or misuse</li>
            <li>Comply with applicable Indian laws and lawful requests</li>
            <li>Provide customer support</li>
          </ul>
          <p style={{ marginTop: '10px' }}>
            We do <strong>not</strong> sell or rent your personal information to third parties.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>4. WhatsApp Messaging (Meta WhatsApp Cloud API)</h2>
          <p>
            We may use the <strong>WhatsApp Business Platform (Cloud API)</strong> to communicate with you
            for service-related purposes such as program updates, reminders, account alerts, and support.
          </p>
          <p>
            By providing your phone number and opting in, you consent to receive WhatsApp messages from CalorienSweat.
            You can opt out at any time by replying <strong>STOP</strong> or contacting us using the details in Section 12.
          </p>
          <p>
            WhatsApp messages are sent in accordance with Meta platform policies. Message delivery is subject to WhatsApp availability.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>5. Information Sharing</h2>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>5.1 With Your Consent</h3>
          <p>We may share your information when you explicitly consent to such sharing.</p>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>5.2 With Your Coach</h3>
          <p>
            If you are a client, we share relevant program-related information with your assigned coach
            to help provide guidance and track progress.
          </p>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>5.3 Service Providers</h3>
          <p>
            We may share information with trusted service providers who help us operate our platform, such as:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
            <li>Cloud hosting and storage providers</li>
            <li>Messaging providers (e.g., WhatsApp Business Platform)</li>
            <li>Monitoring, analytics, and security service providers</li>
            <li>Payment processors (if/when applicable)</li>
          </ul>

          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>5.4 Legal Requirements</h3>
          <p>
            We may disclose information if required by applicable Indian law or to protect the rights, safety,
            and security of users and our platform.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>6. Data Security</h2>
          <p>
            We implement reasonable security practices and procedures to protect your personal information against
            unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul style={{ marginLeft: '20px' }}>
            <li>Encryption of data in transit and at rest (where applicable)</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Regular security assessments and updates</li>
            <li>Logging and monitoring for suspicious activity</li>
          </ul>
          <p>
            However, no method of transmission over the internet or electronic storage is completely secure.
            While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>7. Data Retention</h2>
          <p>
            We retain personal information only as long as necessary to provide services and for legitimate business
            purposes such as security, compliance, and dispute resolution, unless a longer retention period is required
            or permitted by law.
          </p>
          <p>
            When we no longer need your information, we will securely delete or anonymize it.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>8. Your Rights and Choices</h2>
          <p>You may request the following, subject to applicable Indian law:</p>
          <ul style={{ marginLeft: '20px' }}>
            <li><strong>Access:</strong> Request access to your personal information</li>
            <li><strong>Correction:</strong> Request correction of inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account/data (subject to legal requirements)</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for WhatsApp communication and certain processing</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information in Section 12.
          </p>
        </section>
        
        <section style={{ marginBottom: '30px' }}>
  <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>
    Data Deletion Instructions
  </h2>
  <p>
    You have the right to request deletion of your personal data from CalorienSweat.
  </p>
  <p>
    To request data deletion, please follow one of the options below:
  </p>
  <ul style={{ marginLeft: '20px' }}>
    <li>
      Send an email to <strong>privacy@caloriensweat.com</strong> with the subject line
      <strong> “Data Deletion Request”</strong>
    </li>
    <li>
      Include your registered email address and phone number for verification
    </li>
    <li>
      Alternatively, reply <strong>STOP</strong> on WhatsApp and contact our support team
    </li>
  </ul>
  <p>
    Once verified, we will delete or anonymize your personal data within a reasonable
    timeframe, unless retention is required by applicable Indian law.
  </p>
</section>


        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>9. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar technologies to enhance your experience and improve platform performance.
            Cookies may help us:
          </p>
          <ul style={{ marginLeft: '20px' }}>
            <li>Remember your preferences and settings</li>
            <li>Maintain secure sessions</li>
            <li>Understand usage patterns to improve the platform</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling cookies may impact certain functionality.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>10. Children’s Privacy</h2>
          <p>
            CalorienSweat is not intended for individuals under 18 years of age. We do not knowingly collect
            personal information from minors. If you believe a minor has provided us information, please contact us
            and we will take appropriate steps.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the updated policy on this page and
            update the "Last Updated" date. Your continued use of CalorienSweat after changes indicates acceptance
            of the updated policy.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>12. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div style={{
            backgroundColor: 'var(--pale-blue)',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '15px'
          }}>
            <p><strong>CalorienSweat</strong></p>
            <p>Email: info@mindyards.com</p>
            <p>Location: India</p>
            <p>Address: [C-1203,Vastu River Nest,Ravet Pune 412101]</p>
            <p>Phone: [+91-9850341496]</p>
          </div>
          <p style={{ marginTop: '15px' }}>
            We will respond within a reasonable timeframe in accordance with applicable Indian laws.
          </p>
        </section>

        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>13. Legal Compliance (India)</h2>
          <p>
            This Privacy Policy is intended to comply with applicable Indian laws, including the Information Technology Act, 2000
            and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
          </p>
        </section>

        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          padding: '20px',
          backgroundColor: 'var(--gray-light)',
          borderRadius: '8px',
          fontSize: '14px',
          color: 'var(--gray-dark)'
        }}>
          <p>
            This Privacy Policy is effective as of January 29, 2026 and applies to users of the CalorienSweat platform.
          </p>
          <p>
            For the most current version, please visit this page regularly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
